/**
 * One list per report. Table columns and filter-bar controls both come from here.
 *
 * To drop a field later, set standard to false (or delete the row).
 * column: true  -> locked table column; sample file uses sampleHeaders when a display column splits into more than one upload column
 * filter: true  -> locked filter on the bar and in the Filters drawer
 * control       -> how a filter is drawn: search, institute, or pill
 *
 * Person fields read profile / user_institute. Report fields read that report's tables.
 */

import { ABILITY_MARKER_FIELDS } from './reportRegistry';

export type ReportKey = 'events' | 'rewards' | 'markers';
export type FieldControl = 'search' | 'institute' | 'pill';
export type FilterGroup = 'common' | 'report';

export type ReportFieldDef = {
    key: string;
    label: string;
    source: string;
    standard: boolean;
    column: boolean;
    filter: boolean;
    control?: FieldControl;
    /** common stays on the top bar when pill is on. report sits on the "This report" row. */
    filterGroup?: FilterGroup;
    /** Label on the filter bar. The table can keep a shorter column label. */
    filterLabel?: string;
    /** Starts on the bar. False means it begins in Available filters. */
    startApplied?: boolean;
    pill?: boolean;
    /** One header, or several when the table shows one combined column. */
    sampleHeaders?: string[];
};

export type FilterPlacement = {
    key: string;
    label: string;
    group: FilterGroup;
    control?: FieldControl;
    pill: boolean;
    startApplied: boolean;
};

/** Fixed identity columns on every sample file. Not filters and not table columns. */
export const SAMPLE_IDENTITY = [
    { header: 'uuid', source: 'user.id' },
    { header: 'email_id', source: 'user.registered_email_id' },
    { header: 'institute_id', source: 'user_institute.institute_id' },
] as const;

function personFields(): ReportFieldDef[] {
    return [
        {
            key: 'q',
            label: 'Name, UUID, email',
            source: 'profile.name, user.registered_email_id, user.id',
            standard: true,
            column: false,
            filter: true,
            control: 'search',
        },
        {
            key: 'institute',
            label: 'Institute Name',
            filterLabel: 'Campus',
            source: 'user_institute.institute_id',
            standard: true,
            column: false,
            filter: true,
            control: 'institute',
            filterGroup: 'common',
            pill: true,
        },
        {
            key: 'program',
            label: 'Program',
            source: 'user_institute.program_id',
            standard: true,
            column: false,
            filter: true,
            control: 'pill',
            filterGroup: 'common',
            pill: true,
        },
        {
            key: 'pass_out_year',
            label: 'Batch Year',
            source: 'user_institute.pass_out_year',
            standard: true,
            column: false,
            filter: true,
            control: 'pill',
            filterGroup: 'common',
            pill: true,
        },
        {
            key: 'user_status',
            label: 'Profile Status',
            source: 'user_institute.user_status_code',
            standard: true,
            column: false,
            filter: true,
            control: 'pill',
            filterGroup: 'common',
            pill: true,
        },
        {
            key: 'alumni',
            label: 'Alumni',
            source: 'profile.name',
            standard: true,
            column: true,
            filter: false,
            sampleHeaders: ['Alumni'],
        },
        {
            key: 'program_batch',
            label: 'Program / Batch',
            source: 'user_institute.program_id + user_institute.pass_out_year',
            standard: true,
            column: true,
            filter: false,
            sampleHeaders: [],
        },
    ];
}

export const REPORT_FIELDS: Record<ReportKey, ReportFieldDef[]> = {
    events: [
        ...personFields(),
        { key: 'event_name', label: 'Event Name', source: 'rpt_event.name', standard: true, column: true, filter: true, control: 'pill', filterGroup: 'report', startApplied: false, sampleHeaders: ['Event Name'] },
        { key: 'event_type', label: 'Type', filterLabel: 'Event Type', source: 'rpt_event.event_type_opt', standard: true, column: true, filter: true, control: 'pill', filterGroup: 'report', pill: true, sampleHeaders: ['Type'] },
        { key: 'role', label: 'Role', source: 'rpt_event_participation.role_opt', standard: true, column: true, filter: true, control: 'pill', filterGroup: 'report', pill: true, sampleHeaders: ['Role'] },
        { key: 'event_date', label: 'Date', filterLabel: 'Event Date', source: 'rpt_event.start_date', standard: true, column: true, filter: true, control: 'pill', filterGroup: 'report', pill: true, sampleHeaders: ['Date'] },
        { key: 'mode', label: 'Mode', source: 'rpt_event.mode_opt', standard: true, column: true, filter: true, control: 'pill', filterGroup: 'report', pill: true, sampleHeaders: ['Mode'] },
    ],
    rewards: [
        ...personFields(),
        { key: 'title', label: 'Award / Recognition', source: 'rpt_award.title', standard: true, column: true, filter: true, control: 'pill', filterGroup: 'report', pill: true, sampleHeaders: ['Award / Recognition'] },
        { key: 'awarding_body', label: 'Awarding Body', source: 'rpt_award.awarding_body_opt', standard: true, column: true, filter: true, control: 'pill', filterGroup: 'report', pill: true, sampleHeaders: ['Awarding Body'] },
        { key: 'category', label: 'Category', source: 'rpt_award.category_opt', standard: true, column: true, filter: true, control: 'pill', filterGroup: 'report', pill: true, sampleHeaders: ['Category'] },
        { key: 'level', label: 'Level', source: 'rpt_award.level_opt', standard: true, column: true, filter: true, control: 'pill', filterGroup: 'report', pill: true, sampleHeaders: ['Level'] },
        { key: 'award_year', label: 'Year', filterLabel: 'Award Year', source: 'rpt_award.award_year', standard: true, column: true, filter: true, control: 'pill', filterGroup: 'report', pill: true, sampleHeaders: ['Year'] },
    ],
    markers: [
        ...personFields(),
        { key: 'markers', label: 'Markers', source: 'profile_markers', standard: true, column: true, filter: false, sampleHeaders: [] },
        ...ABILITY_MARKER_FIELDS.map((field) => ({
            key: field.code,
            label: field.label,
            source: `profile_markers.${field.code}`,
            standard: true as const,
            column: false,
            filter: true,
            control: 'pill' as const,
            filterGroup: 'report' as const,
            pill: false,
            startApplied: false,
        })),
    ],
};

function active(report: ReportKey) {
    return REPORT_FIELDS[report].filter((field) => field.standard);
}

export function tableColumns(report: ReportKey) {
    return active(report).filter((field) => field.column);
}

export function barFilters(report: ReportKey) {
    return active(report).filter((field) => field.filter);
}

export function filterCatalog(report: ReportKey): FilterPlacement[] {
    return active(report)
        .filter((field) => field.filter && field.control !== 'search')
        .map((field) => ({
            key: field.key,
            label: field.filterLabel || field.label,
            group: field.filterGroup || 'common',
            control: field.control,
            pill: Boolean(field.pill),
            startApplied: field.startApplied !== false,
        }));
}

export function sampleHeaders(report: ReportKey, customHeaders: string[]) {
    // Ability Markers: identity + custom only. Marker values come from profile_markers, not the sheet.
    if (report === 'markers') {
        return ['uuid', 'registered_email_id', 'institute_id', ...customHeaders];
    }
    return [
        ...SAMPLE_IDENTITY.map((column) => column.header),
        ...tableColumns(report).flatMap((field) => (field.sampleHeaders ? field.sampleHeaders : [field.label])),
        ...customHeaders,
    ];
}
